import {Box, Button, Card, CardContent, CssBaseline, Fade, Typography} from "@mui/material";
import {ThemeProvider} from "@mui/material/styles";
import {muiTheme} from "./theme/theme";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {completedSetupCustomer, submitBankConnected} from "../../api/customerSetup";
import {fetchPlaiditems} from "../../api/plaid";
import {fetchCurrentProfile} from "../../api/profile";
import LinkPlaidButton from "../plaid/linkPlaidMUI";
import {Footer} from "./elements/footer";
import {FixedHeader} from "./elements/header";
import {BankLinkInfo} from "./elements/practiceSetupPart2";

export const createCustomerOnboardingId = (id) => {
  try {
    localStorage.setItem("customerOnboardingId", id);
  } catch {}
  window.dispatchEvent(new Event("customerOnboardingId-changed"));
};

export const removeCustomerOnboardingId = () => {
  try {
    localStorage.removeItem("customerOnboardingId");
  } catch {}
  window.dispatchEvent(new Event("customerOnboardingId-changed"));
};

const SuccessOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  return (
    <Fade in={open} timeout={300} unmountOnExit>
      <Box
        sx={{
          fontFamily: "Lato",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
        }}
      >
        <Box
          sx={{
            fontFamily: "Lato",
            backgroundColor: "background.paper",
            borderRadius: "12px",
            boxShadow: 8,
            padding: "32px 32px",
            textAlign: "center",
            width: "90%",
            maxWidth: "400px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src="/Success Animation.gif"
            alt="Setup Complete"
            sx={{
              width: { xs: "120px", sm: "160px" },
              height: "auto",
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h6"
            fontSize={"1.5rem"}
            fontWeight={600}
            sx={{ fontFamily: "Lato" }}
          >
            ✅ You’re All Set!
          </Typography>
          <Typography
            variant="body2"
            fontSize={"16px"}
            sx={{
              mt: 1,
              mb: 3,
              fontFamily: "Lato",
              color: "text.secondary",
            }}
          >
            Your bank accounts are securely linked. Let’s put your financial
            data to work!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onClose();
              const stored = localStorage.getItem("customerOnboardingId");
              sessionStorage.setItem("selectedCustomerId", stored);
              navigate("/bank-activity", { replace: true });
              localStorage.removeItem("skipped_bank_setup");
              removeCustomerOnboardingId();
            }}
            sx={{
              textTransform: "none",
              px: "32px",
              py: "6px",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#4C63D7" },
            }}
          >
            Proceed to Dashboard
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default function BankSetupOnly({
  profile,
  setProfile,
  customer_id = null,
}) {
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(() => {
    const stored = localStorage.getItem("customerOnboardingId");
    if (!stored || stored === "null") return customer_id;
    return stored;
  });

  const [bankLinked, setBankLinked] = useState(null);
  const [checkingPlaid, setCheckingPlaid] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const refreshPlaidItemList = async (id) => {
    if (!id) return;

    try {
      const plaidItems = await fetchPlaiditems(id);
      setBankLinked(Array.isArray(plaidItems) && plaidItems.length > 0);
    } catch (err) {
      console.error("Error fetching plaid items", err);
    }
  };

  useEffect(() => {
    if (!customer) {
      setCheckingPlaid(false);
      setBankLinked(false);
      return;
    }

    let cancelled = false;

    const checkPlaidStatus = async () => {
      setCheckingPlaid(true);
      try {
        const plaidItems = await fetchPlaiditems(customer);
        if (!cancelled) {
          setBankLinked(Array.isArray(plaidItems) && plaidItems.length > 0);
        }
      } catch (err) {
        console.error("❌ Error fetching plaid items:", err);
      } finally {
        if (!cancelled) setCheckingPlaid(false);
      }
    };

    checkPlaidStatus();

    return () => {
      cancelled = true;
    };
  }, [customer]);

  useEffect(() => {
    if (customer) createCustomerOnboardingId(customer);
    else removeCustomerOnboardingId();
  }, [customer]);

  useEffect(() => {
    if (!profile) return;

    const storedId = localStorage.getItem("customerOnboardingId");
    let customerId = storedId ? Number(storedId) : null;

    if (customerId) {
      setCustomer(customerId);
      return;
    }
  }, [profile]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(81.17deg, #121621 -27.35%, #536DEC 142.61%)",
          fontFamily: "Lato",
        }}
      >
        <FixedHeader />

        <Box
          sx={{
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "24px",
            pt: "116px",
            pb: "35px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "24px",
              pt: "auto",
              pb: "auto",
            }}
          >
            <Card
              sx={{
                width: {
                  xs: "calc(100% - 50px)",
                  sm: "calc(100% - 50px)",
                  md: "100%",
                },
                height: "100%",
                maxWidth: "800px",
                maxHeight: "64vh",
                minHeight: "400px",
                borderRadius: "16px",
                border: "1px solid #21212114",
                boxShadow: "0px 2px 4px 0px #00000013",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Content */}
              <CardContent
                sx={{
                  pt: "24px",
                  px: "24px",
                  pb: "24px !important",
                  overflowY: "auto",
                }}
              >
                <SuccessOverlay
                  open={showSuccess}
                  onClose={() => setShowSuccess(false)}
                />

                <BankLinkInfo />
              </CardContent>

              <Box
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  gap: 2,
                  height: { xs: "auto", sm: "64px" },
                  flexShrink: 0,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                    sx={{
                      color: "#536DEC",
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                    onClick={() => navigate("/logout")}
                  >
                    Return To Login
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "row" },
                    gap: 2,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      textTransform: "none",
                      fontWeight: 500,
                      width: "100%",
                      justifyContent: "flex-start",
                    }}
                    onClick={() => navigate("/logout")}
                  >
                    Return To Login
                  </Button>

                  <LinkPlaidButton
                    customer_id={customer}
                    bankLinked={bankLinked}
                    checkingPlaid={checkingPlaid}
                    completeSetup={true}
                    useBankAccountsDialog={true}
                    onBankLinkedChange={async (linked) => {
                      try {
                        setBankLinked(linked);
                        if (!linked) return;

                        await refreshPlaidItemList(customer);

                        setShowSuccess(true);

                        await submitBankConnected({}, customer);

                        await completedSetupCustomer(customer);

                        const [currentProfile] = await fetchCurrentProfile();
                        setProfile(currentProfile);
                      } catch (err) {
                        console.error("Bank link flow failed:", err);
                      }
                    }}
                  />
                </Box>
              </Box>
            </Card>
          </Box>{" "}
          <Footer />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
